import React, { useEffect, useState } from 'react';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import Loading from '$/components/loading/Loading';
import { DataSiteService } from '$/services/DataSiteService';
import * as Bixby from '$/core';
import BixButton from '$/components/global/bixButton';
import { ReactComponent as DownloadIcon } from '$/assets/icons/download.svg';
import LastModifiedFloatingFilterComponent from './lastModifiedFilterComponent';
import './index.scss';

interface IDataSiteDocumentsProps {
  dispatchToastError: (message: string) => void;
  dispatchToastSuccess: (message: string) => void;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => void;
}

const DataSiteDocumentsGrid: React.FC<IDataSiteDocumentsProps> = props => {
  const { dispatchToastError, dispatchToastSuccess, dispatchDownloadDocument } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState(Bixby.Enums.DataSiteDocumentFilter.LastHour);
  const [dataSiteDocumentsError, setDataSiteDocumentsError] = useState(undefined as string | undefined);
  const [dataSiteDocuments, setDataSiteDocuments] = useState([] as Bixby.Models.DataSiteDocument[]);

  const getDataSiteDocumentsAsync = async (filter: Bixby.Enums.DataSiteDocumentFilter) => {
    setIsLoading(true);
    setDataSiteDocumentsError(undefined);
    try {
      const dataSiteDocuments: Bixby.Models.DataSiteDocument[] = await DataSiteService.getDataSiteDocumentsAsync(
        filter
      );
      setDataSiteDocuments(dataSiteDocuments);
    } catch (e: any) {
      setDataSiteDocumentsError('Error retrieving data site documents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDataSiteDocumentsAsync(filter);
  }, [filter]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const downloadDataSiteDocuments = async (e: React.MouseEvent<HTMLButtonElement>, params: any) => {
    const target = e.currentTarget;

    // get names of files to download
    let fileNames: string[] = [];
    if (params.node.group) {
      params.node.allLeafChildren.forEach((leafNode: any) => {
        if (leafNode.data && leafNode.data.fileName) {
          fileNames.push(leafNode.data.fileName);
        }
      });
    } else {
      fileNames = [params.data['fileName']];
    }

    try {
      if (target) {
        target.disabled = true;
      }

      const result = await DataSiteService.downloadDataSiteDocumentsAsync(fileNames);
      if (result) {
        const { blob, filename } = result;
        if (filename) {
          dispatchDownloadDocument(blob, filename.split(';')[0]);
        }
        dispatchToastSuccess('Downloaded Data Site Files.');
      } else {
        dispatchToastError('Unable to download files.');
      }
    } catch (e: any) {
      dispatchToastError('Unable to download files.');
    } finally {
      if (target) {
        target.disabled = false;
      }
    }
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <div className="data-site-documents-grid-container">
        {!Bixby.Utils.isNullOrEmpty(dataSiteDocumentsError) && <p className="error">{dataSiteDocumentsError}</p>}

        {Bixby.Utils.isNullOrEmpty(dataSiteDocumentsError) && (
          <div className="data-site-documents-data-grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  getContextMenuItems={getContextMenuItems}
                  onGridReady={onGridReady}
                  rowData={dataSiteDocuments}
                  localeText={{ noRowsToShow: 'No data site documents to display' }}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                  }}
                  autoGroupColumnDef={{
                    headerName: 'Date Site Grouping',
                  }}
                  rowHeight={32}
                  headerHeight={32}
                  columnDefs={[
                    {
                      field: 'bixbyId',
                      filter: 'agTextColumnFilter',
                      headerName: 'Bixby ID',
                      rowGroup: true,
                      enableRowGroup: true,
                    },
                    {
                      field: 'clientName',
                      filter: 'agTextColumnFilter',
                      headerName: 'Client Name',
                      rowGroup: true,
                      enableRowGroup: true,
                    },
                    {
                      field: 'documentName',
                      filter: 'agTextColumnFilter',
                      headerName: 'Document Name',
                    },
                    {
                      field: 'lastModified',
                      headerName: 'Last Modified',
                      sort: 'desc',
                      cellRenderer: (params: any) => {
                        return params.value ? dayjs(params.value).format('MM/DD/YYYY HH:mm') : '';
                      },
                      filter: 'agDateColumnFilter',
                      suppressMenu: true,
                      floatingFilter: true,
                      floatingFilterComponent: LastModifiedFloatingFilterComponent,
                      floatingFilterComponentParams: {
                        suppressFilterButton: true,
                        currentValue: filter,
                        onSortChange: (value: Bixby.Enums.DataSiteDocumentFilter) => {
                          setFilter(value);
                        },
                      },
                    },
                    {
                      headerName: 'Actions',
                      maxWidth: 150,
                      filter: 'agTextColumnFilter',
                      cellRenderer: (params: any) => {
                        return (
                          <BixButton
                            useSmall
                            className="download_all"
                            type="button"
                            onClick={e => downloadDataSiteDocuments(e, params)}>
                            <DownloadIcon />
                          </BixButton>
                        );
                      },
                    },
                  ]}
                  rowGroupPanelShow={'always'}
                  groupDefaultExpanded={1}></AgGridReact>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default DataSiteDocumentsGrid;
